import Parse from "parse";
import { useEffect, useState } from "react";
import {
  SIV_Order,
  SIV_Package,
  SIV_PackageItem,
  SIV_PackageText,
} from "../../../parsegenerated";

export const usePackages = (order?: SIV_Order | null) => {
  const [packages, setPackages] = useState<SIV_Package[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!order) {
      setPackages([]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const query = new Parse.Query(SIV_Package);
        query.equalTo("order", order);
        query.include("deliveryNote");
        query.include("tenant");
        query.descending("packagingDate");
        const results = await query.find();
        setPackages(results);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [order?.id]);

  return { packages, loading, error };
};

export const usePackageItems = (pkg?: SIV_Package | null) => {
  const [items, setItems] = useState<SIV_PackageItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!pkg) {
      setItems([]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const query = new Parse.Query(SIV_PackageItem);
        query.equalTo("package", pkg);
        query.include("tenant");
        query.include("goodsReceiptProcessPosition");
        const results = await query.find();
        setItems(results);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [pkg?.id]);

  return { items, loading, error };
};

export const usePackageTexts = (pkg?: SIV_Package | null) => {
  const [texts, setTexts] = useState<SIV_PackageText[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!pkg) {
      setTexts([]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const query = new Parse.Query(SIV_PackageText);
        query.equalTo("package", pkg);
        query.ascending("no");
        const results = await query.find();
        setTexts(results);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [pkg?.id]);

  return { texts, loading, error };
};

export const usePackage = (id?: string) => {
  const [pkg, setPkg] = useState<SIV_Package | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!id) {
      setPkg(null);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const query = new Parse.Query(SIV_Package);
        query.include("order");
        query.include("deliveryNote");
        query.include("tenant");
        const result = await query.get(id);
        setPkg(result);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  return { package: pkg, loading, error };
};
